import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { ArrowLeft } from 'lucide-react';
import { PublicShell } from '../components/PublicShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { publicUrl, hreflangAlternates, activeLang, ogLocaleOf } from '../lib/publicSeo';

const SECTIONS = [
  'use',
  'userContent',
  'account',
  'availability',
  'termination',
  'liability',
  'governingLaw',
  'changes',
  'contact',
] as const;

const ROUTE = '/terms';
const OG_IMAGE = 'https://markflow.fyi/og.png';

export function TermsPage() {
  const { t, i18n } = useTranslation();
  const lang = activeLang(i18n.resolvedLanguage);
  const pageUrl = publicUrl(ROUTE, lang);

  usePageMeta({
    title: t('terms.meta.title'),
    description: t('terms.meta.description'),
    canonical: pageUrl,
    ogTitle: t('terms.meta.ogTitle'),
    ogDescription: t('terms.meta.ogDescription'),
    ogImage: OG_IMAGE,
    ogUrl: pageUrl,
    htmlLang: lang,
    ogLocale: ogLocaleOf(lang),
    ogLocaleAlternate: ogLocaleOf(lang === 'uk' ? 'en' : 'uk'),
    alternates: hreflangAlternates(ROUTE),
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: t('terms.meta.title'),
      description: t('terms.meta.description'),
      url: pageUrl,
    },
  });

  return (
    <PublicShell>
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-6">
          <Link href="/" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('footer.backToApp')}
          </Link>

          <div className="space-y-2">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
              {t('terms.title')}
            </h1>
            <p className="text-xs text-zinc-500">{t('terms.lastUpdated')}</p>
          </div>
          <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed">{t('terms.intro')}</p>

          {SECTIONS.map((key) => (
            <section key={key} className="space-y-3">
              <h2 className="text-xl font-semibold">{t(`terms.sections.${key}.title`)}</h2>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed whitespace-pre-line">
                {t(`terms.sections.${key}.body`)}
              </p>
            </section>
          ))}
        </div>
      </main>
    </PublicShell>
  );
}
